import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { Modal } from 'react-bootstrap';
import * as Yup from 'yup';
import {
  FiBriefcase,
  FiHome,
  FiImage,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSave,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';

import { useAuth } from '../../../hooks/auth';
import api from '../../../services/api';
import { useToast } from '../../../hooks/useToast';

import {
  AvatarPicker,
  Button,
  Field,
  FieldGroup,
  ModalBody,
  RemoveAvatarBtn,
  SectionLabel,
} from './styles';

interface IPartnerCategory {
  id: string;
  title: string;
}

interface IState {
  uf: string;
  nome: string;
}

interface ICity {
  id: number;
  nome: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

interface IPartner {
  id: string;
  name: string;
  address: string;
  address2: string;
  postal_code: string;
  city_id: number;
  state: string;
  whatsapp: string;
  phone: string;
  email: string;
  avatar: string;
  description: string;
  category_id: string;
  syndicate_id: string;
}

interface IProps {
  partnerId: string;
  showModal: boolean;
  handleShowModal: (value: boolean) => void;
  handlePartners: (partners: []) => void;
  handleLoading: (value: boolean) => void;
}

const cepMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d)/, '$1-$2');
};

const phoneMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
};

const EditPartnerModal: React.FC<IProps> = ({
  partnerId,
  showModal,
  handleShowModal,
  handlePartners,
  handleLoading,
}) => {
  const { addToast } = useToast();
  const { user } = useAuth();

  const [partnerInfo, setPartnerInfo] = useState<IPartner>({} as IPartner);
  const [partnersCategories, setPartnersCategories] = useState<
    IPartnerCategory[]
  >([]);
  const [states, setStates] = useState<IState[]>([]);
  const [selectedState, setSelectedState] = useState('');
  const [cities, setCities] = useState<ICity[]>([]);
  const [unions, setUnions] = useState<ISyndicate[]>([]);

  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [avatarSrc, setAvatarSrc] = useState('');
  const [avatarName, setAvatarName] = useState('');

  const handleClose = useCallback(() => {
    setSelectedState('');
    setCities([]);
    setAvatarSrc('');
    setAvatarName('');
    setPartnerInfo({} as IPartner);
    handleShowModal(false);
    setShow(false);
  }, [handleShowModal]);

  const handleUpdatePartner = useCallback(
    async values => {
      const { avatar, ...rest } = values;
      const partnerData = new FormData();
      if (avatar && typeof avatar !== 'string') {
        partnerData.append('avatar', avatar);
      }
      partnerData.append('document', JSON.stringify(rest));

      setSubmitting(true);
      try {
        handleLoading(true);
        await api.patch(`/unions-partners/${partnerId}`, partnerData);
        const updatedList = await api.get('/unions-partners');
        handlePartners(updatedList.data);

        handleClose();
        addToast({
          title: 'Sucesso',
          body: 'Convênio editado com sucesso.',
        });
      } catch {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao tentar editar o convênio.',
        });
      } finally {
        handleLoading(false);
        setSubmitting(false);
      }
    },
    [addToast, handleLoading, handleClose, handlePartners, partnerId],
  );

  useEffect(() => {
    if (selectedState) {
      const loadCities = async () => {
        const loadedCities = await api.get(`/ibge/cities/${selectedState}`);
        setCities(loadedCities.data);
      };
      loadCities();
    }
  }, [selectedState]);

  useEffect(() => {
    if (!showModal) return;

    const loadData = async () => {
      const loadedPartnerInfo = await api.get(`/unions-partners/${partnerId}`);
      setPartnerInfo(loadedPartnerInfo.data);
      setSelectedState(loadedPartnerInfo.data.state);

      if (loadedPartnerInfo.data.avatar) {
        setAvatarSrc(loadedPartnerInfo.data.avatar);
      }

      const [cats, sts] = await Promise.all([
        api.get('/unions-partners/categories'),
        api.get('/ibge/states'),
      ]);
      setPartnersCategories(cats.data);
      setStates(sts.data);

      if (user.role === 'admin') {
        const loadUnions = await api.get('/unions');
        setUnions(loadUnions.data);
      }

      setShow(true);
    };

    loadData();
  }, [user, partnerId, showModal]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required('Nome é obrigatório'),
        address: Yup.string().required('Endereço é obrigatório'),
        address2: Yup.string().required('Bairro/complemento é obrigatório'),
        postal_code: Yup.string().required('CEP é obrigatório'),
        city_id: Yup.number().required('Cidade é obrigatória'),
        state: Yup.string().required('Estado é obrigatório'),
        whatsapp: Yup.string().nullable(),
        phone: Yup.string().nullable(),
        email: Yup.string().email('E-mail inválido').nullable(),
        description: Yup.string().nullable(),
        category_id: Yup.string().required('Categoria é obrigatória'),
        syndicate_id: Yup.string().required('Sindicato é obrigatório'),
      }),
    [],
  );

  if (!partnerInfo) return null;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      aria-labelledby="edit-partner-modal"
    >
      <Formik
        enableReinitialize
        initialValues={{
          name: partnerInfo.name || '',
          address: partnerInfo.address || '',
          address2: partnerInfo.address2 || '',
          postal_code: partnerInfo.postal_code || '',
          city_id: partnerInfo.city_id || 0,
          state: partnerInfo.state || '',
          whatsapp: partnerInfo.whatsapp || '',
          phone: partnerInfo.phone || '',
          email: partnerInfo.email || '',
          avatar: partnerInfo.avatar || '',
          description: partnerInfo.description || '',
          category_id: partnerInfo.category_id || '',
          syndicate_id: partnerInfo.syndicate_id || '',
        }}
        onSubmit={handleUpdatePartner}
        validationSchema={validationSchema}
      >
        {({
          values,
          handleSubmit,
          handleChange,
          setFieldValue,
          handleBlur,
          errors,
          touched,
        }) => (
          <>
            <Modal.Header closeButton>
              <Modal.Title
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                Editar convênio
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <ModalBody as="form" onSubmit={handleSubmit}>
                <SectionLabel>
                  <FiBriefcase size={11} />
                  Identificação
                </SectionLabel>

                <FieldGroup $cols={3}>
                  <Field style={{ gridColumn: 'span 2' }}>
                    <label htmlFor="name">
                      Nome *
                      <input
                        id="name"
                        name="name"
                        placeholder="Digite o nome do convênio"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.name && errors.name && (
                      <span className="error">{errors.name}</span>
                    )}
                  </Field>
                  <Field>
                    <label htmlFor="category_id">
                      Categoria *
                      <select
                        id="category_id"
                        name="category_id"
                        value={values.category_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <option value="">Selecione</option>
                        {partnersCategories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    {touched.category_id && errors.category_id && (
                      <span className="error">{errors.category_id}</span>
                    )}
                  </Field>
                </FieldGroup>

                <SectionLabel>
                  <FiMapPin size={11} />
                  Endereço
                </SectionLabel>

                <FieldGroup>
                  <Field>
                    <label htmlFor="address">
                      Endereço *
                      <input
                        id="address"
                        name="address"
                        placeholder="Rua e número"
                        value={values.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.address && errors.address && (
                      <span className="error">{errors.address}</span>
                    )}
                  </Field>
                  <Field>
                    <label htmlFor="address2">
                      Bairro e complemento *
                      <input
                        id="address2"
                        name="address2"
                        placeholder="Bairro, complemento"
                        value={values.address2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.address2 && errors.address2 && (
                      <span className="error">{errors.address2}</span>
                    )}
                  </Field>
                </FieldGroup>

                <FieldGroup $cols={3}>
                  <Field>
                    <label htmlFor="state">
                      Estado *
                      <select
                        id="state"
                        name="state"
                        value={values.state}
                        onChange={e => {
                          setFieldValue('state', e.target.value);
                          setFieldValue('city_id', '');
                          setSelectedState(e.target.value);
                        }}
                        onBlur={handleBlur}
                      >
                        <option value="">Selecione</option>
                        {states.map(state => (
                          <option value={state.uf} key={state.uf}>
                            {state.uf}
                            {' · '}
                            {state.nome}
                          </option>
                        ))}
                      </select>
                    </label>
                    {touched.state && errors.state && (
                      <span className="error">{errors.state}</span>
                    )}
                  </Field>
                  <Field>
                    <label htmlFor="city_id">
                      Cidade *
                      <select
                        id="city_id"
                        name="city_id"
                        value={values.city_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!values.state}
                      >
                        <option value="">
                          {values.state
                            ? 'Selecione a cidade'
                            : 'Selecione o estado'}
                        </option>
                        {cities.map(city => (
                          <option value={city.id} key={city.id}>
                            {city.nome}
                          </option>
                        ))}
                      </select>
                    </label>
                    {touched.city_id && errors.city_id && (
                      <span className="error">{errors.city_id}</span>
                    )}
                  </Field>
                  <Field>
                    <label htmlFor="postal_code">
                      CEP *
                      <input
                        id="postal_code"
                        name="postal_code"
                        placeholder="00000-000"
                        value={values.postal_code}
                        maxLength={9}
                        onChange={e =>
                          setFieldValue('postal_code', cepMask(e.target.value))}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.postal_code && errors.postal_code && (
                      <span className="error">{errors.postal_code}</span>
                    )}
                  </Field>
                </FieldGroup>

                <SectionLabel>
                  <FiPhone size={11} />
                  Contato
                </SectionLabel>

                <FieldGroup $cols={3}>
                  <Field>
                    <label htmlFor="phone">
                      Telefone
                      <input
                        id="phone"
                        name="phone"
                        placeholder="(00) 0000-0000"
                        maxLength={15}
                        value={values.phone}
                        onChange={e =>
                          setFieldValue('phone', phoneMask(e.target.value))}
                        onBlur={handleBlur}
                      />
                    </label>
                  </Field>
                  <Field>
                    <label htmlFor="whatsapp">
                      WhatsApp
                      <input
                        id="whatsapp"
                        name="whatsapp"
                        placeholder="(00) 00000-0000"
                        maxLength={15}
                        value={values.whatsapp}
                        onChange={e =>
                          setFieldValue('whatsapp', phoneMask(e.target.value))}
                        onBlur={handleBlur}
                      />
                    </label>
                  </Field>
                  <Field>
                    <label htmlFor="email">
                      E-mail
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="contato@exemplo.com"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.email && errors.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </Field>
                </FieldGroup>

                <SectionLabel>
                  <FiMail size={11} />
                  Descrição
                </SectionLabel>

                <FieldGroup $cols={1}>
                  <Field>
                    <label htmlFor="description">
                      Descrição do convênio
                      <textarea
                        id="description"
                        name="description"
                        rows={4}
                        placeholder="Detalhes, benefícios, observações..."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                  </Field>
                </FieldGroup>

                <SectionLabel>
                  <FiImage size={11} />
                  Logotipo
                </SectionLabel>

                <AvatarPicker $hasImage={!!avatarSrc}>
                  {avatarSrc ? (
                    <div className="thumb">
                      <img src={avatarSrc} alt={avatarName || 'Logo'} />
                    </div>
                  ) : (
                    <div className="placeholder">
                      <FiImage size={22} />
                    </div>
                  )}
                  <div className="info">
                    <div className="title">
                      {avatarSrc ? 'Logo selecionado' : 'Selecione um logotipo'}
                    </div>
                    <div className="hint" title={avatarName}>
                      {avatarName || 'PNG ou JPG, até 5MB'}
                    </div>
                    <span className="action">
                      <FiUploadCloud size={11} />
                      {avatarSrc ? 'Trocar imagem' : 'Enviar imagem'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setAvatarSrc(URL.createObjectURL(file));
                      setAvatarName(file.name);
                      setFieldValue('avatar', file);
                    }}
                  />
                  {avatarSrc && (
                    <RemoveAvatarBtn
                      type="button"
                      aria-label="Remover logo"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAvatarSrc('');
                        setAvatarName('');
                        setFieldValue('avatar', '');
                      }}
                    >
                      <FiX size={12} />
                    </RemoveAvatarBtn>
                  )}
                </AvatarPicker>

                {user.role === 'admin' && (
                  <>
                    <SectionLabel>
                      <FiHome size={11} />
                      Sindicato
                    </SectionLabel>

                    <FieldGroup $cols={1}>
                      <Field>
                        <label htmlFor="syndicate_id">
                          Sindicato *
                          <select
                            id="syndicate_id"
                            name="syndicate_id"
                            value={values.syndicate_id}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Selecione</option>
                            {unions.map(syndicate => (
                              <option value={syndicate.id} key={syndicate.id}>
                                {syndicate.nome_fantasia}
                              </option>
                            ))}
                          </select>
                        </label>
                        {touched.syndicate_id && errors.syndicate_id && (
                          <span className="error">{errors.syndicate_id}</span>
                        )}
                      </Field>
                    </FieldGroup>
                  </>
                )}
              </ModalBody>
            </Modal.Body>

            <Modal.Footer>
              <Button $variant="secondary" type="button" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => handleSubmit()}
                disabled={submitting}
              >
                <FiSave size={14} />
                {submitting ? 'Salvando...' : 'Salvar alterações'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default EditPartnerModal;
